import { Button, Icon, PageModel, ResourceEdit, TextField, Typography } from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import { ChangeEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { TFileManagerFile } from "../../../../components/beeEditor/types"
import { setDataInPath } from "../../../../utils/data"
import { getImageInfo } from "../../../../utils/images"
import "./styles.scss"
import { TPageSlugFaviconEdit } from "./types"

export function PageSlugFaviconEdit({ page, savePage, setPage, toggleFileManager, fileManagerActionRef }: TPageSlugFaviconEdit) {
    const [cSlug, setCSlug] = useState(page.slug || "")
    const [faviconError, setFaviconError] = useState<string>("")

    const { t } = useTranslation()

    function replaceInvalidCharacters(value: string) {
        return value
            .toLowerCase()                    // Convert to lowercase
            .replace(/\s+/g, "-")             // Replace spaces with hyphens
            .replace(/[^a-z0-9-]/g, "")       // Remove all non-alphanumeric characters except hyphens
            .replace(/-+/g, "-")              // Replace multiple consecutive hyphens with a single hyphen
            .replace(/^-+|-+$/g, "")          // Remove leading and trailing hyphens
    }

    function handleFieldChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const { value } = event.target
        setCSlug(value)
    }

    function handleFieldChangeTimeout(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const { name, value } = event.target
        const finalValue = replaceInvalidCharacters(value)
        const dataToUpdate = setDataInPath({ name, value: finalValue, data: page.seo, merge: true })

        savePage({
            dataToUpdate,
            render: false
        })
    }

    function handleFileInsert(file: TFileManagerFile) {
        fileManagerActionRef.current = undefined
        toggleFileManager(false)
        setFaviconError("")

        getImageInfo(file["public-url"]).then((imageInfo) => {
            if (imageInfo.width === 32 && imageInfo.height === 32) {
                // Image dimensions are valid, save the favicon
                const dataToUpdate = setDataInPath({
                    name: "seo.favicon",
                    value: file["public-url"],
                    data: page.seo
                })

                setPage(new PageModel({ ...page, ...dataToUpdate }))
                savePage({
                    dataToUpdate
                })
            } else {
                // Image dimensions are invalid
                setFaviconError(t("pages-details-favicon-dimension-error", {
                    width: imageInfo.width,
                    height: imageInfo.height
                }))
            }
        }).catch((error) => {
            setFaviconError(t("pages-details-favicon-load-error"))
        })
    }

    function handleToggleFileManager() {
        fileManagerActionRef.current = handleFileInsert
        toggleFileManager()
    }

    const hasInfo = page.seo?.favicon || page.slug

    return <ResourceEdit
        className="pageSlugFaviconEditResource"
        title={t("pages-details-slug-fav-title")}
        desc={t("pages-details-slug-fav-desc")}
        editable
        status={
            hasInfo ? "valid" : "empty"
        }
        support={hasInfo &&
            <Stack direction="row" gap={4} alignItems="center">
                {page.seo?.favicon && <img className="favicon-preview" src={page.seo.favicon} />}
                <Typography variant="body1">{page.published_url}</Typography>
            </Stack>
        }
        editableContent={
            <Stack gap={6}>
                <Stack gap={6}>
                    <TextField
                        name="slug"
                        label={t("general-slug")}
                        placeholder={t("pages-details-slug-placeholder")}
                        value={cSlug}
                        onChange={handleFieldChange}
                        timeout={{
                            callback: handleFieldChangeTimeout
                        }}
                    />
                    <Stack gap={2}>
                        <Stack className="favicon-preview-container" direction="row" gap={8} justifyContent="space-between" alignItems="center">
                            <Stack direction="row" gap={4} alignItems="center" justifyContent="center">
                                {page.seo?.favicon && <img key={page.seo.favicon} className="favicon-preview preview" src={page.seo.favicon} />}
                                {!page.seo?.favicon && <Icon className="favicon-preview preview" name="Image18" />}
                                <Typography variant="body1S" className="bold">{t("pages-details-favicon-upload-desc")}</Typography>
                            </Stack>
                            <Button
                                variant="contained"
                                size="small"
                                color="secondary"
                                onClick={handleToggleFileManager}
                            >
                                {t("general-upload-image")}
                            </Button>
                        </Stack>
                        {faviconError && (
                            <Typography variant="body2" style={{ color: "#d32f2f" }}>
                                {faviconError}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        }
    />

}

